/**
 * Blogs & Articles Management Page
 * - Lists all blog posts and articles
 * - Supports pagination and status filtering
 * - Allows creating, editing, and publishing blogs
 * - Export functionality for blog data
 */

import React, { useEffect } from "react";
import { MdOutlineArrowBackIos } from "react-icons/md";
import { FiDownload, FiPlus } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchBlogs } from "../../redux/slices/blogsCommunitySlice";
import TableComponent from "../../components/common/TableComponent";

const statusClasses = {
  draft: "bg-yellow-100 text-yellow-600",
  published: "bg-green-100 text-green-800",
  archived: "bg-red-100 text-red-800",
  Approved: "bg-green-100 text-green-800",
  Rejected: "bg-red-100 text-red-800",
  Pending: "bg-yellow-50 text-yellow-600",
  Default: "bg-gray-100 text-gray-600",
};

export default function BlogsCommunity() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const blogs = useSelector((state) => state.blogsCommunity.items);
  const status = useSelector((state) => state.blogsCommunity.status);
  const pagination = useSelector((state) => state.blogsCommunity.pagination);

  useEffect(() => {
    dispatch(fetchBlogs(pagination.currentPage));
  }, [dispatch, pagination.currentPage]);

  const handlePageChange = (page) => {
    dispatch(fetchBlogs(page));
  };

  const columns = [
    { key: "title", label: "Title" },
    { key: "author", label: "Author" },
    { key: "category", label: "Category" },
    { key: "submitted", label: "Published" },
    {
      key: "status",
      label: "Status",
      render: (val) => (
        <span
          className={`rounded-md px-3 py-1 text-xs font-semibold ${
            statusClasses[val] || statusClasses.Default
          }`}
        >
          {val ? val.charAt(0).toUpperCase() + val.slice(1).toLowerCase() : 'Unknown'}
        </span>
      ),
    },
  ];

  const renderCell = (key, value) => {
    if (key === "status") {
      return (
        <span
          className={`rounded-md px-3 py-1 text-xs font-semibold ${
            statusClasses[value] || statusClasses.Default
          }`}
        >
          {value ? value.charAt(0).toUpperCase() + value.slice(1).toLowerCase() : 'Unknown'}
        </span>
      );
    }
    return value;
  };

  const handleRowClick = (row) => {
    navigate(`/swift-app/admin/blogsCommunity/${row.uuid}`);
  };

  // const handleExportClick = () => {
  //   navigate(`/swift-app/admin/export?tab=BlogsCommunity`);
  // };

  return (
    <div className="px-3 sm:px-6 md:px-8 lg:px-12 pt-6 md:pt-8 font-inter min-h-screen bg-white">
      {/* ✅ Header */}
      <div className="flex flex-col gap-4">
        {/* Top Row — Back button + Title + Create Button */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              aria-label="Go Back"
              className="p-2 md:p-3 text-lg md:text-xl text-gray-800 hover:text-blue-600"
            >
              <MdOutlineArrowBackIos />
            </button>
            <h1 className="text-lg sm:text-xl md:text-2xl font-semibold text-gray-900">
              Blogs and Article
            </h1>
          </div>

          {/* ✅ Create Blog button */}
          <button
            onClick={() => navigate("/swift-app/admin/blogs/new")}
            className="flex items-center justify-center gap-2 bg-blue-500 text-white font-medium px-3 sm:px-4 md:px-5 py-2 rounded-lg shadow hover:bg-blue-600 transition"
          >
            <FiPlus className="w-5 h-5" />
            <span className="hidden sm:inline">Create Blog</span>
          </button>
        </div>

        {/* Subtitle */}
        <p className="text-gray-600 text-sm sm:text-base ml-10 md:ml-14">
          Manage blog posts and articles
        </p>
      </div>

      {/* ✅ Table */}
      <div className="mt-8 md:mt-10 overflow-x-auto">
        {status === "loading" ? (
          <div className="text-center text-gray-500 py-10 text-sm md:text-base">
            Loading...
          </div>
        ) : (
          <TableComponent
            columns={columns}
            data={blogs}
            renderCell={renderCell}
            onRowClick={handleRowClick}
            currentPage={pagination.currentPage}
            totalPages={pagination.totalPages}
            totalItems={pagination.totalItems}
            itemsPerPage={pagination.itemsPerPage}
            onPageChange={handlePageChange}
          />
        )}
      </div>
    </div>
  );
}
